/**
 * Pictures in the previous look (spec 027 D-26, D-30, D-31): the Look row's
 * line and the modal that opens after Edit look saves a changed look. Both
 * offer **Discard** (one `clearPictures` step through
 * `storyStore.discardPictures`, one story history entry; the media stay in
 * the library) and **Discard and draft new** (the same, then Draft all on
 * the discarded slots that have a brief, each card waiting for its own pick
 * and Accept). Keeping is a full choice: nothing else checks `oldLook`.
 * No copy says "free", "included" or "not charged" (D-2).
 */
import React, { useEffect, useRef, useState } from 'react';
import type { CharacterFraming, PictureSlot } from '../../../../Shared/src/@types';
import { DEFAULT_DRAFT_COUNT, jobUnitsNeeded, type OldLookView } from '../../../../Shared/src/images';
import { imageOwnerOf, useImageGenStore } from '../../stores/imageGenStore';
import { ownerActions, usePictureOwner, withNoun } from '../pictures/pictureOwner';
import { generateTooltip, needsText, skippedText } from './imageGenText';
import styles from './ImageGen.module.scss';

const pictures = (n: number) => `${n} ${n === 1 ? 'picture' : 'pictures'}`;

export const DISCARD_TEXT = {
  line: (n: number) => `${pictures(n)} ${n === 1 ? 'is' : 'are'} in the previous look.`,
  modal: (n: number) => `${pictures(n)} in this story ${n === 1 ? 'was' : 'were'} drawn in the previous look.`,
  keep: 'Keep them',
  discard: 'Discard',
  discardAndDraft: 'Discard and draft new',
  close: 'Close',
  discarded: (n: number) => `${pictures(n)} discarded. ${n === 1 ? 'It stays' : 'They stay'} in your library.`,
  draftCost: (slots: number, count = DEFAULT_DRAFT_COUNT) =>
    `${pictures(slots)} (${slots * count} drafts) · Needs ${jobUnitsNeeded(slots, count)} of this month's limit`,
  noBrief: 'None of these pictures has a brief to draft from.',
  failed: "The pictures couldn't be discarded. Try again.",
} as const;

/** The discarded slots Discard and draft new drafts: those with a brief, with their framings (D-31). */
export function draftPlanOf(oldLook: OldLookView, slots: readonly PictureSlot[]): {
  slotIds: string[]; framings: Record<string, CharacterFraming>; skipped: number;
} {
  const byId = new Map(slots.map((s) => [s.id, s]));
  const briefed = oldLook.slotIds.map((id) => byId.get(id)).filter((s): s is PictureSlot => !!s && (s.brief ?? '').trim().length > 0);
  const framings = Object.fromEntries(briefed.filter((s) => s.kind === 'character' && s.framing).map((s) => [s.id, s.framing!]));
  return { slotIds: briefed.map((s) => s.id), framings, skipped: oldLook.slotIds.length - briefed.length };
}

/**
 * Discards `oldLook`'s pictures and, with `draft`, starts Draft all on the
 * briefed ones; then reloads the look so the line goes. Resolves with the
 * number of pictures discarded, or `false` when the step failed.
 */
export async function discardOldLook(oldLook: OldLookView, draft: boolean): Promise<number | false> {
  // The plan is read before the discard: briefs and framings do not change with it.
  // The story's or (spec 030) the scene's own map and discard.
  const actions = ownerActions(imageOwnerOf(useImageGenStore.getState()));
  const plan = draftPlanOf(oldLook, actions.map().slots);
  const result = await actions.discardPictures(oldLook.mediaIds);
  if (!result.ok) return false;
  // The discard is saved (the apply route writes it) before the jobs start, so no old-look picture anchors a draft.
  if (draft && plan.slotIds.length > 0) {
    await useImageGenStore.getState().generate(plan.slotIds, Object.keys(plan.framings).length > 0 ? { framings: plan.framings } : {});
  }
  await useImageGenStore.getState().loadLook();
  return oldLook.slotIds.length;
}

/** One story (or scene) undo puts every discarded picture back; the look is reloaded so the line returns. */
export async function undoDiscard(): Promise<void> {
  await ownerActions(imageOwnerOf(useImageGenStore.getState())).undo();
  await useImageGenStore.getState().loadLook();
}

/** Discard and draft new, then Discard, with the cost line and the skipped note. */
const DiscardButtons: React.FC<{
  oldLook: OldLookView;
  onDiscarded: (count: number) => void;
  where: 'row' | 'modal';
}> = ({ oldLook, onDiscarded, where }) => {
  const settings = useImageGenStore((s) => s.settings);
  const slots = usePictureOwner().map.slots;
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const plan = draftPlanOf(oldLook, slots);
  const canDraw = !!settings && settings.mode !== 'off' && settings.available;
  const limit = canDraw && plan.slotIds.length > 0 ? generateTooltip(settings!, plan.slotIds.length) : undefined;
  const cost = plan.slotIds.length > 0 ? DISCARD_TEXT.draftCost(plan.slotIds.length) : DISCARD_TEXT.noBrief;
  const needed = jobUnitsNeeded(plan.slotIds.length);
  const short = !!limit?.disabled;
  const draftLine = short ? needsText(needed, settings!.limit.left) : cost;

  const run = async (draft: boolean) => {
    setBusy(true);
    setError(null);
    try {
      const n = await discardOldLook(oldLook, draft);
      if (n === false) setError(DISCARD_TEXT.failed);
      else onDiscarded(n);
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <div className={styles.row}>
        {canDraw && (
          <button
            type="button"
            className={styles.primary}
            disabled={busy || short || plan.slotIds.length === 0}
            title={draftLine}
            data-tooltip={draftLine}
            onClick={() => { void run(true); }}
            data-testid={`${where}-discard-draft`}
          >
            {DISCARD_TEXT.discardAndDraft}
          </button>
        )}
        <button type="button" className={styles.button} disabled={busy} onClick={() => { void run(false); }} data-testid={`${where}-discard`}>
          {DISCARD_TEXT.discard}
        </button>
      </div>
      {canDraw && <p className={styles.muted} data-testid={`${where}-discard-cost`}>{draftLine}</p>}
      {canDraw && plan.skipped > 0 && plan.slotIds.length > 0 && (
        <p className={styles.muted} data-testid={`${where}-discard-skipped`}>{skippedText(plan.skipped)}</p>
      )}
      {error && <p className={styles.error} role="alert">{error}</p>}
    </>
  );
};

/** The Look row's line (D-30): information with its buttons, never a gate (D-31). */
export const OldLookLine: React.FC<{ oldLook: OldLookView; onDiscarded: (count: number) => void }> = ({ oldLook, onDiscarded }) => (
  <div className={styles.oldLookLine} data-testid="look-old-line">
    <p>{DISCARD_TEXT.line(oldLook.slotIds.length)}</p>
    <DiscardButtons oldLook={oldLook} onDiscarded={onDiscarded} where="row" />
  </div>
);

/** The modal after a look change (D-30): Keep them (also Escape and ✕), Discard, Discard and draft new. */
export const DiscardOldLookModal: React.FC<{
  oldLook: OldLookView;
  onClose: () => void;
  onDiscarded: (count: number) => void;
}> = ({ oldLook, onClose, onDiscarded }) => {
  const ref = useRef<HTMLDivElement>(null);
  const noun = usePictureOwner().noun;
  useEffect(() => {
    ref.current?.querySelector<HTMLElement>('button:not([disabled])')?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        onClose();
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);
  return (
    <div className={styles.modalBackdrop}>
      <div ref={ref} className={styles.modal} role="dialog" aria-modal="true" aria-labelledby="discard-old-look-title" data-testid="discard-modal">
        <button type="button" className={styles.modalClose} aria-label={DISCARD_TEXT.close} onClick={onClose} data-testid="discard-modal-close">×</button>
        <p id="discard-old-look-title">{withNoun(DISCARD_TEXT.modal(oldLook.slotIds.length), noun)}</p>
        <DiscardButtons oldLook={oldLook} onDiscarded={onDiscarded} where="modal" />
        <div className={styles.row}>
          <button type="button" className={styles.button} onClick={onClose} data-testid="discard-keep">{DISCARD_TEXT.keep}</button>
        </div>
      </div>
    </div>
  );
};
